import { Component, forwardRef, Input, OnDestroy, OnInit, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DTypeForm, DTypeFormArray, DTypeTableField } from '@grupo-constant/next-dtype/form';
import { CODEBARSCANNERWITHSELECT_URI, DTypeCol, DTypeViewableCol, SELECTFACADE_COLUMN_URI, SELECTFROMCONCEPTSERVICE_COLUMN_URI, SELECTFROMENUMERATION_COLUMN_URI, SELECTFROMMASTERDATA_COLUMN_URI, SELECTFROMNEXTSERVICIOPLANNINGAREA_COLUMN_URI, SELECTFROMURI_COLUMN_URI, SELECTMASTER_COLUMN_URI, SELECTNEXTTASKTYPE_COLUMN_URI, SELECTWITHGIVENOPTIONS_COLUMN_URI } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { Subject, takeUntil } from 'rxjs';
import { TableRowDialogComponent } from '../../table-row.dialog.component';
import { DtypeItemComponent } from '../../../../dtype-item/dtype-item.component';
import { LocalizePipe } from '../../../../../pipes/standalone/localize.pipe';
import { DTypeFormFieldComponent } from '../../../../dtype-field.model';
import { LocaleString } from '../../../../../translate/localeString.model';
import { DTypeService } from '../../../../dtype.service';
import { LocalizeService } from '../../../../../pipes/localize.service';
import { FuseConfirmationService } from '../../../../../../../@fuse/services/confirmation/confirmation.service';
import { DialogsService } from '../../../../../../dialogs/dialogs.service';

@Component({
    selector: 'tablewithvariablerows-component',
    templateUrl: './tablewithvariablerows.component.html',
    standalone: true,
    imports: [forwardRef(() => DtypeItemComponent), LocalizePipe, MatButtonModule, MatIconModule],
    styles: [
        /* language=SCSS */
        `
            .error-panel {
                border-style: solid;
                border-width: 1px;
                border-color: red;
            }
        `],
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class DtypeTableWithVariableRowsComponent implements OnInit, OnDestroy, DTypeFormFieldComponent {
    @ViewChildren(MatExpansionPanel) panels: MatExpansionPanel;

    @Input() field: DTypeTableField;
    @Input() isUpdate: boolean;
    @Input() useSummaryView: boolean;

    panel: number;
    oldPanel: number;
    canAddRow: boolean = true;
    rowsSave: number[] = [];
    saving: boolean = false;

    tableHeader: LocaleString[] = [];
    tableData: any[][] = [];

    urisSelects = [
        SELECTFACADE_COLUMN_URI,
        SELECTFROMCONCEPTSERVICE_COLUMN_URI,
        SELECTFROMENUMERATION_COLUMN_URI,
        SELECTFROMURI_COLUMN_URI,
        SELECTMASTER_COLUMN_URI,
        SELECTWITHGIVENOPTIONS_COLUMN_URI,
        SELECTFROMMASTERDATA_COLUMN_URI,
        SELECTFROMNEXTSERVICIOPLANNINGAREA_COLUMN_URI,
        SELECTNEXTTASKTYPE_COLUMN_URI,
        CODEBARSCANNERWITHSELECT_URI
    ];

    editView: boolean = false;
    repeatRows: boolean = false;
    required: boolean = false;

    total: { name: string, total: number }[];

    private _unsubscribeAll = new Subject<void>();


    constructor(
        private dtypeService: DTypeService,
        private localize: LocalizeService,
        private fuseConfirmationService: FuseConfirmationService,
        private transloco: TranslocoService,
        private matDialog: MatDialog,
        private snackBar: MatSnackBar,
        private dialogsService: DialogsService,
        private _fuseConfirmationService: FuseConfirmationService
    ) { }

    async ngOnInit() {
        if (this.field.minRows > 0) {
            this.required = true;
        }

        this.repeatRows = this.field.allowRepeatedRows;
        const readonlyOnUpdate = (this.field.dTypeCol as DTypeViewableCol).readonlyOnUpdate;
        if (readonlyOnUpdate && this.isUpdate) {
            this.field.readonly = true;
        }
        if ((!this.field.nameIndex || this.field.nameIndex.length === 0) && (!this.field.uniqueIndex || this.field.uniqueIndex.length === 0)) {
            this.warningDialog(this.transloco.translate('complexNameIndexNeeded', { caption: this.localize.transform((this.field.dTypeCol as DTypeViewableCol).caption) }));
        }

        if (!this.field.allowRepeatedRows && (!this.field.uniqueIndex || this.field.uniqueIndex.length === 0)) {
            this.warningDialog(this.transloco.translate('complexUniqueIndexNeeded', { caption: this.localize.transform((this.field.dTypeCol as DTypeViewableCol).caption) }));
        }

        this.calculateTotalSum();
        if (this.field.embeddedForms.length > 0 && this.field.embeddedForms.length < this.field.minRows) {
            this.field.control.setErrors({'incorrect': true});
        } else {
            this.field.control.setErrors({'incorrect': false});
            this.field.control.updateValueAndValidity();
        }
        this.field.control.valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.calculateTotalSum();
            });
    }

    ngOnDestroy() {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    getColumnTotal(column: DTypeCol) {
        return this.total.find(t => t.name === column.name)?.total;
    }

    someRowHasError() {
        for (const embeddedForms of this.field.embeddedForms) {
            if (!embeddedForms.control.valid && embeddedForms.control.touched) {
                return true;
            }
        }
        return false;
    }

    async addItem() {
        const dialog = this.matDialog.open(TableRowDialogComponent, {
            data: { dtype: this.field.table.dtype, isUpdate: this.isUpdate },
            panelClass: 'table-row-dialog-panel',
            width: '90vw',
            height: '80vh',
            maxWidth: '1536px',
        });
        dialog.afterClosed().subscribe((newForm: DTypeForm) => {
            if (newForm) {
                this.field.embeddedForms.push(newForm);
                (this.field.control as DTypeFormArray).push(newForm.control);
            }
            if (this.field.embeddedForms.length > 0 && this.field.embeddedForms.length >= this.field.minRows) {
                this.field.control.setErrors({'incorrect': false});
                this.field.control.updateValueAndValidity();
            } else {
                this.field.control.setErrors({'incorrect': true});
            }
        });
    }

    async editItem(row: DTypeForm) {
        const dialog = this.matDialog.open(TableRowDialogComponent, {
            data: { dtype: this.field.table.dtype, values: row.control.getRawValue(), isUpdate: this.isUpdate },
            panelClass: 'table-row-dialog-panel',
            width: '90vw',
            height: '80vh',
            maxWidth: '1536px',
        });
        dialog.afterClosed().subscribe((updatedForm: DTypeForm) => {
            if (updatedForm) {
                row.control.patchValue(updatedForm.control.getRawValue());
            }
        });
    }

    async removeItem(event: PointerEvent, index: number) {
        event.stopPropagation();
        const confirm = await this.openConfirmDialog(this.transloco.translate('removeExistingRow'), this.transloco.translate('accept'), this.transloco.translate('cancel'));
        if (confirm) {
            this.field.embeddedForms.splice(index, 1);
            (this.field.control as DTypeFormArray).removeAt(index);
        }
    }

    private calculateTotalSum() {
        if (this.field.totalFields.length > 0) {
            this.total = [];
            this.field.totalFields.map((col) => {
                let tot = 0;
                for (const val of this.field.control.value) {
                    const valNum = val[col] === undefined ? 0 : val[col];
                    tot += valNum;
                }
                this.total.push({ name: col, total: tot });
            });
        }
    }

    private warningDialog(message: string) {
        this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.transloco.translate('accept')
                },
                cancel: {
                    label: this.transloco.translate('cancel'),
                    show: false
                }
            }
        });
    }

    private openConfirmDialog(message: string, confirmButton: string, cancelButton: string) {
        const dialogRef = this.fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: this.transloco.translate(message),
            actions: {
                confirm: {
                    label: this.transloco.translate(confirmButton)
                },
                cancel: {
                    label: this.transloco.translate(cancelButton)
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe(result => resolve(result === 'confirmed'));
        });
    }

}


